/* Contractor Portal shell — dark "fleet ops" chrome with a sky accent so it reads as its own
   product surface (distinct from the orange staff console and the light customer portal). */
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Map as MapIcon, Truck, ClipboardList, UserCog, LogOut, Menu, X, ShieldCheck, Factory, Link2, Construction, ClipboardPlus } from "lucide-react";
import { useContractorAuth } from "@/context/ContractorAuthContext";

const NAV = [
  { to: "/contractor/suppliers", label: "My Suppliers", icon: Factory, testId: "ctr-nav-suppliers" },
  { to: "/contractor/invite", label: "Invite Suppliers", icon: Link2, testId: "ctr-nav-invite" },
  { to: "/contractor/fleet", label: "Fleet Map", icon: MapIcon, testId: "ctr-nav-fleet" },
  { to: "/contractor/jobs", label: "Raise a Job", icon: ClipboardPlus, testId: "ctr-nav-jobs", kind: "pump" },
  { to: "/contractor/pumps", label: "My Pumps", icon: Construction, testId: "ctr-nav-pumps", kind: "pump" },
  { to: "/contractor/trucks", label: "My Trucks", icon: Truck, testId: "ctr-nav-trucks" },
  { to: "/contractor/loads", label: "Loads", icon: ClipboardList, testId: "ctr-nav-loads" },
  { to: "/contractor/account", label: "Account", icon: UserCog, testId: "ctr-nav-account" },
];

function Brand() {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 bg-[#38BDF8] flex items-center justify-center shadow-lg shadow-sky-900/40">
        <Truck className="w-4 h-4 text-[#0B0D10]" />
      </div>
      <div>
        <div className="text-[9px] tracking-[0.25em] text-[#8B909A]">PARTNER PORTAL</div>
        <div className="heading text-sm font-black tracking-wide text-white">ARMORBATCH <span className="text-[#38BDF8]">GLOBAL</span></div>
      </div>
    </div>
  );
}

export default function ContractorShell() {
  const { user, logout } = useContractorAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); nav("/contractor/login", { replace: true }); };

  return (
    <div className="contractor-theme h-screen flex flex-col lg:flex-row bg-[#0B0D10] text-[#E5E7EB] overflow-hidden" data-testid="contractor-shell">
      <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-[#22262E] bg-[#0F1216]">
        <Brand />
        <button data-testid="ctr-mobile-nav-toggle" onClick={() => setOpen(!open)} className="p-2 text-white">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <aside className={`${open ? "block" : "hidden"} lg:flex lg:flex-col w-full lg:w-64 shrink-0 bg-[#0F1216] border-r border-[#22262E]`}>
        <div className="hidden lg:block px-5 py-6 border-b border-[#22262E]"><Brand /></div>
        <nav className="py-3 flex-1">
          {NAV.filter((n) => !n.kind || n.kind === (user?.kind || "haulage")).map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testId} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-5 py-3 text-sm border-l-2 transition-colors ${
                isActive ? "bg-[#38BDF8]/10 text-[#38BDF8] border-[#38BDF8] font-bold" : "text-[#A1A6B0] border-transparent hover:bg-[#161A20] hover:text-white"}`}>
              <n.icon className="w-4 h-4" />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-5 py-4 border-t border-[#22262E]">
          <div className="flex items-center gap-1.5 text-[10px] tracking-widest text-[#8B909A] mb-1"><ShieldCheck className="w-3 h-3 text-emerald-400" /> PRIVATE FLEET VIEW</div>
          <div className="text-sm font-bold truncate text-white" data-testid="ctr-signed-in-name">{user?.contractor_name || user?.name}</div>
          <div className="text-[11px] text-[#8B909A] truncate mb-3">{user?.email}</div>
          <button data-testid="ctr-logout-btn" onClick={doLogout}
            className="w-full inline-flex items-center justify-center gap-2 border border-[#2C313A] text-[#A1A6B0] hover:bg-[#38BDF8] hover:text-[#0B0D10] hover:border-[#38BDF8] uppercase text-xs tracking-widest py-2 transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 min-h-0 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
